import { useCallback, useRef } from "react";
import { streamChat } from "../lib/api";
import { useChatsStore } from "../store/chats";
import { useSettingsStore } from "../store/settings";
import { clearToken } from "../lib/storage";

/**
 * Per-session map of AbortControllers. Это глобальная (модульная) ссылка,
 * чтобы пережить переключение активной сессии и любые ре-рендеры компонента.
 * Ключ — sessionId, значение — контроллер активного стрима.
 */
const streamControllers: Map<string, AbortController> = new Map();
const watchdogIds: Map<string, ReturnType<typeof setInterval>> = new Map();

export function useChat() {
  // Ref на случай если кому-то нужен объект (для совместимости),
  // но реально логика работает через streamControllers map.
  const ctrlRef = useRef<AbortController | null>(null);

  const activeSession = useChatsStore((s) => s.sessions.find((ss) => ss.id === s.activeId));
  const append = useChatsStore((s) => s.appendUserMessage);
  const start = useChatsStore((s) => s.startAssistant);
  const ingest = useChatsStore((s) => s.ingestEvent);

  const send = useCallback(async (text: string, opts?: { voice?: boolean }) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    // Захватываем sessionId до append/start: append идёт в текущую активную
    // (что и хочет пользователь), и мы потом отслеживаем именно её.
    const sessionId = useChatsStore.getState().activeId;

    // Если в ЭТОЙ ЖЕ сессии уже идёт стрим — отменяем его (пользователь
    // отправил ещё одно сообщение в этом же чате).
    const prev = streamControllers.get(sessionId);
    if (prev) {
      prev.abort();
      streamControllers.delete(sessionId);
    }
    const prevW = watchdogIds.get(sessionId);
    if (prevW) {
      clearInterval(prevW);
      watchdogIds.delete(sessionId);
    }

    const ctrl = new AbortController();
    streamControllers.set(sessionId, ctrl);
    ctrlRef.current = ctrl;
    append(trimmed);
    const aId = start();

    // Watchdog: если 120 сек нет ни одного события — abort + error.
    let lastEventAt = Date.now();
    const watchdogId = setInterval(() => {
      const idle = Date.now() - lastEventAt;
      if (idle > 120_000) {
        console.warn(`[useChat] watchdog: no SSE events for 120s in session ${sessionId}, aborting`);
        ctrl.abort();
      }
    }, 10_000);
    watchdogIds.set(sessionId, watchdogId);
    const onAnyEvent = () => { lastEventAt = Date.now(); };

    try {
      await streamChat({
        message: trimmed,
        sessionId,
        reasoning: "auto",
        voice: opts?.voice,
        // Голосовой разговор — всегда чат (без тулов/сервера), что бы ни стояло в displayMode.
        mode: opts?.voice ? "chat" : useSettingsStore.getState().displayMode,
        onEvent: (evt) => {
          onAnyEvent();
          ingest(aId, evt);
        },
        signal: ctrl.signal,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("401") || msg.toLowerCase().includes("unauthorized")) {
        // Сессия истекла. Чистим локальный токен и уводим пользователя через
        // централизованный SSO-логаут account.aicores.ru, который вернёт нас
        // обратно в чат после повторной авторизации. До редиректа короткое
        // сообщение, чтобы переход не выглядел как «страница случайно обновилась».
        console.warn("[useChat] получили 401 от chat/stream:", msg);
        useChatsStore.getState().finalizeAssistant(
          aId,
          "_🔒 Сессия истекла — перенаправляем на повторный вход…_",
        );
        try { clearToken(); } catch { /* ignore */ }
        const returnTo = encodeURIComponent(window.location.href);
        setTimeout(() => {
          window.location.href =
            "https://account.aicores.ru/logout?return_to=https://account.aicores.ru/login%3Freturn_to%3D" +
            returnTo;
        }, 800);
        return;
      }
      if (ctrl.signal.aborted) {
        const idle = Date.now() - lastEventAt;
        const stuck = idle > 90_000;
        const fallbackText = stuck
          ? "_(агент завис: нет ответа от сервера 2 минуты, попробуйте переформулировать или проверить VPS-подключение)_"
          : "_(остановлено пользователем)_";
        useChatsStore.getState().finalizeAssistant(aId, fallbackText);
        return;
      }
      ingest(aId, { type: "error", phase: "error", text: msg });
    } finally {
      // Чистим только если это наш контроллер (не перезаписан новой отправкой).
      if (streamControllers.get(sessionId) === ctrl) {
        streamControllers.delete(sessionId);
      }
      const wd = watchdogIds.get(sessionId);
      if (wd === watchdogId) {
        clearInterval(wd);
        watchdogIds.delete(sessionId);
      } else if (wd) {
        // на случай если уже заменили — clear безопасно, но не трогаем мапу.
        // (просто игнорируем)
      } else {
        clearInterval(watchdogId);
      }
    }
  }, [append, start, ingest]);

  /**
   * cancel() отменяет стрим ТЕКУЩЕЙ активной сессии. Не трогает другие сессии,
   * которые могут стримить параллельно.
   */
  const cancel = useCallback(() => {
    const st = useChatsStore.getState();
    const sessionId = st.activeId;
    const ctrl = streamControllers.get(sessionId);
    if (ctrl) {
      ctrl.abort();
      streamControllers.delete(sessionId);
    }
    ctrlRef.current = null;
    const active = st.sessions.find((s) => s.id === sessionId);
    if (active && active.status === "streaming") {
      const lastMsg = active.messages[active.messages.length - 1];
      if (lastMsg && lastMsg.role === "assistant") {
        useChatsStore.getState().finalizeAssistant(
          lastMsg.id,
          lastMsg.text || "_(остановлено пользователем)_",
        );
      }
    }
  }, []);

  return {
    send,
    cancel,
    streaming: activeSession?.status === "streaming",
  };
}
